import { useState, useEffect } from 'react';
import useSWR from 'swr';
import Cookies from 'js-cookie';

import { IRepository, IRepositoryEntry } from '@/types';
import { IResponseError } from '@/lib/apiBase';
import { fetchTabularFilesForRepository, ITabularFileResponse } from '@/lib/tabularFileApi';
import { IRepositoryBranch } from '@/lib/repositoryBranchesApi';

interface useTabularFileEntriesParams {
  repository: IRepository;
  branch?: IRepositoryBranch;
  initialData?: ITabularFileResponse | undefined;
}

const useTabularFileEntries = ({ repository, branch, initialData }: useTabularFileEntriesParams) => {
  const branchName = branch?.name || repository.default_branch;

  const [status, setStatus] = useState<'success' | 'error' | 'pending'>('pending');
  const [entries, setEntries] = useState<IRepositoryEntry[]>(initialData?.entries || []);
  const [error, setError] = useState<IResponseError | undefined>();

  const { data, isLoading, isValidating } = useSWR(
    [repository.namespace, repository.name, branchName],
    () =>
      fetchTabularFilesForRepository({
        repository,
        commitRef: branchName,
      }),
    {
      fallbackData: initialData,
      keepPreviousData: true,
      revalidateOnFocus: false,
      revalidateOnMount: !initialData && !!branchName,
      revalidateOnReconnect: false,
    },
  );

  useEffect(() => {
    if (data) {
      setEntries(data?.entries);
      setStatus(data?.status);
      setError(data?.error as IResponseError);
    }
  }, [data]);

  return {
    status: status,
    isLoading: isLoading,
    isValidating: isValidating,
    entries: entries,
    error: error,
  };
};

export default useTabularFileEntries;
